import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    ArrowLeft,
    Download,
    HelpCircle,
    Loader2,
    LogOut,
    RefreshCw,
    ShoppingCart,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import letterService from '../../services/letterService';
import { paymentCredits } from '../../services/adminService';
import { API_URL } from '../../services/api';
import { trackEvent } from '../../utils/analytics';
import { DEV_BYPASS_PAYMENT } from '../../config/dev';
import PaymentGateway from '../payment/PaymentGateway';
import LetterTour from './LetterTour';
import { LETTER_TOUR_STORAGE_KEY } from './letterTourSteps';
import {
    CONTENT_SECTIONS,
    DEFAULT_RECIPIENT,
    LetterContent,
    LetterData,
    LetterSectionKey,
    LETTER_TEMPLATES,
} from '../../types/letter';
import LetterPreview from './LetterPreview';
import SignaturePad from './SignaturePad';

const LetterEditor: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { logout } = useAuth();
    const [letter, setLetter] = useState<LetterData | null>(null);
    const [activeSection, setActiveSection] = useState<LetterSectionKey>('introduction');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [ordering, setOrdering] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [error, setError] = useState('');
    const [savedFlash, setSavedFlash] = useState(false);
    const [hasPaid, setHasPaid] = useState(false);
    // Comment le téléchargement en cours a été débloqué — un crédit gratuit et
    // un paiement mettent tous les deux hasPaid à true, donc l'événement GA
    // unlock_method a besoin de cette distinction séparée pour rester fiable.
    const [unlockMethod, setUnlockMethod] = useState<'paid' | 'free' | null>(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [freeCredits, setFreeCredits] = useState(0);
    const [downloadPrice, setDownloadPrice] = useState(750);
    const [tourOpen, setTourOpen] = useState(false);

    const refreshDownloadPrice = useCallback(() => {
        fetch(`${API_URL}/payment/methods`)
            .then((r) => r.json())
            .then((data) => {
                const letterPrice = Number(data?.letterAmount ?? data?.prices?.letter);
                const fallback = Number(data?.amount);
                if (letterPrice > 0) setDownloadPrice(letterPrice);
                else if (fallback > 0) setDownloadPrice(fallback);
            })
            .catch(() => undefined);
    }, []);

    useEffect(() => {
        if (!id) return;
        letterService
            .get(id)
            .then(setLetter)
            .catch((e) => setError(e?.response?.data?.error || 'Erreur'))
            .finally(() => setLoading(false));
    }, [id]);

    useEffect(() => {
        if (!DEV_BYPASS_PAYMENT) {
            paymentCredits
                .get()
                .then((r) => setFreeCredits(r.freeDownloadsRemaining || 0))
                .catch(() => setFreeCredits(0));
        }
        refreshDownloadPrice();
    }, [refreshDownloadPrice]);

    /** Première visite (ou ?tour=1) : lancer le guide */
    useEffect(() => {
        if (loading) return;
        const force = searchParams.get('tour') === '1';
        try {
            if (!force && localStorage.getItem(LETTER_TOUR_STORAGE_KEY)) return;
        } catch {
            /* ignore */
        }
        const t = window.setTimeout(() => setTourOpen(true), 600);
        return () => window.clearTimeout(t);
    }, [loading, searchParams]);

    useEffect(() => {
        const payment = searchParams.get('payment');
        if (payment === 'success') {
            setHasPaid(true);
            setSearchParams({}, { replace: true });
        } else if (payment === 'failure') {
            setError('Le paiement a échoué ou a été annulé.');
            setSearchParams({}, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const markTourDone = useCallback(() => {
        try {
            localStorage.setItem(LETTER_TOUR_STORAGE_KEY, '1');
        } catch {
            /* ignore */
        }
        setTourOpen(false);
    }, []);

    const startTour = useCallback(() => {
        setTourOpen(true);
    }, []);

    const save = useCallback(
        async (patch: Partial<LetterData>) => {
            if (!id || !letter) return;
            setSaving(true);
            try {
                const updated = await letterService.update(id, patch);
                setLetter(updated);
                setSavedFlash(true);
                setTimeout(() => setSavedFlash(false), 1500);
            } catch (e: any) {
                setError(e?.response?.data?.error || e.message);
            } finally {
                setSaving(false);
            }
        },
        [id, letter]
    );

    const updateContent = (key: keyof LetterContent, value: string) => {
        if (!letter) return;
        const content = { ...letter.content, [key]: value };
        setLetter({ ...letter, content });
    };

    const persistContent = (override?: Partial<LetterContent>) => {
        if (!letter) return;
        const content = override ? { ...letter.content, ...override } : letter.content;
        if (override) {
            setLetter({ ...letter, content });
        }
        save({ content });
    };

    const regenerate = async () => {
        if (!id) return;
        setRegenerating(true);
        setError('');
        try {
            const updated = await letterService.generate(id);
            setLetter(updated);
        } catch (e: any) {
            setError(e?.response?.data?.error || e.message);
        } finally {
            setRegenerating(false);
        }
    };

    const downloadPdf = async () => {
        if (!id || !letter) return;
        setDownloading(true);
        setError('');
        try {
            await letterService.update(id, { content: letter.content, status: 'final' });
            const blob = await letterService.downloadPdf(id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${letter.title || 'Lettre'}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            trackEvent('document_download', {
                document_type: 'letter',
                format: 'pdf',
                template_id: letter.template || '',
                unlock_method: unlockMethod || 'dev_bypass',
            });
            if (!DEV_BYPASS_PAYMENT) {
                setHasPaid(false);
                setUnlockMethod(null);
            }
        } catch (e: any) {
            const msg =
                e?.message && !String(e.message).includes('<!DOCTYPE')
                    ? e.message
                    : e?.response?.data instanceof Blob
                      ? await e.response.data.text().then((t: string) => {
                            try {
                                return JSON.parse(t).error;
                            } catch {
                                return t.includes('<!DOCTYPE')
                                    ? 'Erreur serveur lors du téléchargement PDF'
                                    : t;
                            }
                        })
                      : e?.response?.data?.error || e.message;
            setError(msg || 'Téléchargement impossible');
            if (/paiement|crédit/i.test(String(msg))) {
                setHasPaid(false);
            }
        } finally {
            setDownloading(false);
        }
    };

    const handleOrder = async () => {
        if (!id) return;
        if (DEV_BYPASS_PAYMENT) {
            await downloadPdf();
            return;
        }
        if (ordering) return;
        setOrdering(true);
        setError('');
        try {
            if (letter?.content) {
                await letterService.update(id, { content: letter.content });
            }
            if (freeCredits > 0) {
                const result = await paymentCredits.use(id, 'letter');
                const remaining = result.freeDownloadsRemaining ?? freeCredits - 1;
                setFreeCredits(remaining);
                setHasPaid(true);
                setUnlockMethod('free');
                trackEvent('free_download_used', {
                    document_type: 'letter',
                    credits_remaining: remaining,
                });
                return;
            }
            refreshDownloadPrice();
            setShowPaymentModal(true);
        } catch (err: any) {
            if (String(err.message || '').includes('Aucun téléchargement')) {
                refreshDownloadPrice();
                setShowPaymentModal(true);
            } else {
                setError(err.message || 'Erreur commande');
            }
        } finally {
            setOrdering(false);
        }
    };

    if (loading || !letter) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                {error ? (
                    <p className="text-red-600">{error}</p>
                ) : (
                    <Loader2 className="w-8 h-8 animate-spin text-teal-700" />
                )}
            </div>
        );
    }

    const defaultSubject = letter.answers?.jobTitle
        ? `Objet : Candidature au poste de ${letter.answers.jobTitle}${
              letter.answers.company ? ` — ${letter.answers.company}` : ''
          }`
        : 'Objet : Candidature spontanée';
    const sectionValue =
        activeSection === 'subject'
            ? letter.content?.subject?.trim() || defaultSubject
            : activeSection === 'recipient'
              ? letter.content?.recipient?.trim() || DEFAULT_RECIPIENT
              : activeSection === 'signature'
                ? letter.content?.signature ||
                  `${letter.answers?.firstName || ''} ${letter.answers?.lastName || ''}`.trim()
                : letter.content?.[activeSection] || '';
    const canDownload = DEV_BYPASS_PAYMENT || hasPaid;

    return (
        <div className="min-h-screen bg-slate-100 flex flex-col">
            <header className="bg-white border-b px-4 py-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                    <button
                        type="button"
                        data-tour="letter-logo"
                        onClick={() => navigate('/espace')}
                        className="text-lg font-bold shrink-0"
                    >
                        Flash<span className="text-teal-600">CV</span>
                    </button>
                    <span className="text-sm text-slate-500 truncate max-w-[160px]">{letter.title}</span>
                    {(saving || savedFlash) && (
                        <span className="text-xs text-teal-700 shrink-0">
                            {saving ? 'Enregistrement…' : 'Enregistré'}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        type="button"
                        data-tour="letter-help"
                        onClick={startTour}
                        title="Aide — guide d’utilisation"
                        className="p-2 rounded-lg text-slate-500 hover:text-teal-700 hover:bg-teal-50"
                    >
                        <HelpCircle className="w-5 h-5" />
                    </button>
                    <button
                        type="button"
                        data-tour="letter-regen"
                        onClick={regenerate}
                        disabled={regenerating}
                        className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50"
                    >
                        {regenerating ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <RefreshCw className="w-4 h-4" />
                        )}
                        Régénérer
                    </button>

                    {canDownload ? (
                        <button
                            type="button"
                            data-tour="letter-order"
                            onClick={downloadPdf}
                            disabled={downloading}
                            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-lg bg-teal-700 text-white hover:bg-teal-800"
                        >
                            {downloading ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <Download className="w-4 h-4" />
                            )}
                            Télécharger PDF
                        </button>
                    ) : (
                        <button
                            type="button"
                            data-tour="letter-order"
                            onClick={handleOrder}
                            disabled={ordering}
                            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-lg bg-teal-700 text-white hover:bg-teal-800"
                        >
                            {ordering ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <ShoppingCart className="w-4 h-4" />
                            )}
                            {freeCredits > 0
                                ? `Utiliser un crédit (${freeCredits})`
                                : `Commander · ${downloadPrice} FCFA`}
                        </button>
                    )}
                    <button
                        type="button"
                        data-tour="letter-logout"
                        onClick={logout}
                        className="p-2 text-slate-500"
                    >
                        <LogOut className="w-5 h-5" />
                    </button>
                </div>
            </header>

            {error && (
                <div className="bg-red-50 text-red-700 text-sm px-4 py-2 border-b border-red-100">
                    {error}
                </div>
            )}

            <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
                <aside className="lg:w-64 bg-white border-r p-4 space-y-1 overflow-y-auto">
                    <button
                        type="button"
                        data-tour="letter-answers"
                        onClick={() => navigate(`/creer-lettre?id=${id}`)}
                        className="w-full mb-4 text-left text-sm px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-2"
                    >
                        <ArrowLeft className="w-4 h-4" /> Modifier mes réponses
                    </button>
                    <div data-tour="letter-sections">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-400 px-2 mb-2">
                            Sections
                        </p>
                        {CONTENT_SECTIONS.map((s) => {
                            if (s.key === 'gap' && !letter.answers?.hasGap && !letter.content?.gap) {
                                return null;
                            }
                            return (
                                <button
                                    key={s.key}
                                    type="button"
                                    onClick={() => setActiveSection(s.key)}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm ${
                                        activeSection === s.key
                                            ? 'bg-teal-50 text-teal-900 font-medium'
                                            : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                                >
                                    {s.label}
                                </button>
                            );
                        })}
                    </div>
                    <div className="pt-4 mt-4 border-t" data-tour="letter-style">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-400 px-2 mb-2">
                            Modèle
                        </p>
                        <select
                            className="w-full border border-slate-300 rounded-lg px-2 py-2 text-sm"
                            value={letter.template}
                            onChange={(e) => {
                                const template = e.target.value;
                                setLetter({ ...letter, template });
                                save({ template });
                            }}
                        >
                            {LETTER_TEMPLATES.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.name}
                                </option>
                            ))}
                        </select>
                        <label className="block text-xs font-bold uppercase text-slate-400 px-2 mt-3 mb-1">
                            Couleur
                        </label>
                        <input
                            type="color"
                            value={letter.style?.primaryColor || '#0d9488'}
                            onChange={(e) => {
                                const style = {
                                    ...(letter.style || { fontFamily: 'Georgia, serif' }),
                                    primaryColor: e.target.value,
                                };
                                setLetter({ ...letter, style });
                                save({ style });
                            }}
                            className="ml-2 w-10 h-8 cursor-pointer"
                        />
                    </div>
                </aside>

                <section
                    className="lg:w-80 bg-white border-r p-4 flex flex-col gap-3 self-start w-full lg:sticky lg:top-0 max-h-[calc(100vh-4rem)] overflow-y-auto"
                    data-tour="letter-editor"
                >
                    <h2 className="text-sm font-bold text-slate-800">
                        {CONTENT_SECTIONS.find((s) => s.key === activeSection)?.label}
                    </h2>

                    {activeSection === 'signature' ? (
                        <>
                            <label className="text-xs font-medium text-slate-500">
                                Nom sous la signature
                            </label>
                            <input
                                type="text"
                                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:outline-none"
                                value={sectionValue}
                                onChange={(e) => updateContent('signature', e.target.value)}
                                onBlur={() => persistContent()}
                                placeholder="Prénom NOM"
                            />
                            <SignaturePad
                                value={letter.content?.signatureImage || ''}
                                onChange={(dataUrl) => {
                                    updateContent('signatureImage', dataUrl);
                                    persistContent({ signatureImage: dataUrl });
                                }}
                            />
                        </>
                    ) : (
                        <textarea
                            className={`w-full border border-slate-300 rounded-lg p-3 text-sm leading-relaxed resize-y focus:ring-2 focus:ring-teal-500 focus:outline-none ${
                                activeSection === 'subject' ||
                                activeSection === 'recipient' ||
                                activeSection === 'greeting'
                                    ? 'h-24 min-h-[6rem]'
                                    : 'h-52 min-h-[13rem] max-h-[50vh]'
                            }`}
                            value={sectionValue}
                            onChange={(e) => updateContent(activeSection, e.target.value)}
                            onBlur={() => persistContent()}
                            placeholder={
                                activeSection === 'subject'
                                    ? 'Objet : Candidature au poste de…'
                                    : activeSection === 'recipient'
                                      ? DEFAULT_RECIPIENT
                                      : undefined
                            }
                        />
                    )}

                    <button
                        type="button"
                        onClick={() => persistContent()}
                        disabled={saving}
                        className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm disabled:opacity-60"
                    >
                        {saving ? 'Enregistrement…' : 'Enregistrer la section'}
                    </button>
                </section>

                <main
                    className="flex-1 overflow-auto p-6 flex justify-center bg-slate-200/60"
                    data-tour="letter-preview"
                >
                    <div
                        className="shadow-xl bg-white"
                        style={{ width: 794 * 0.72, height: 1123 * 0.72, overflow: 'hidden' }}
                    >
                        <div style={{ width: 794, transform: 'scale(0.72)', transformOrigin: 'top left' }}>
                            <LetterPreview data={letter} />
                        </div>
                    </div>
                </main>
            </div>

            {showPaymentModal && id && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="relative max-w-3xl w-full">
                        <PaymentGateway
                            letterId={id}
                            documentType="letter"
                            amount={downloadPrice}
                            templateId={letter.template}
                            templateName={
                                LETTER_TEMPLATES.find((t) => t.id === letter.template)?.name || letter.template
                            }
                            onClose={() => setShowPaymentModal(false)}
                            onSuccess={() => {
                                setShowPaymentModal(false);
                                setHasPaid(true);
                                setUnlockMethod('paid');
                            }}
                        />
                    </div>
                </div>
            )}

            <LetterTour open={tourOpen} onClose={markTourDone} />
        </div>
    );
};

export default LetterEditor;
